import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Gift, Zap } from "lucide-react";
import { trackLeadGeneration } from "@/hooks/useAnalytics";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { identifyVisitor } from "@/hooks/useVisitorTracking";
import { z } from "zod";

const exitIntentSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name too long"),
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
});

const ExitIntentPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check if already shown in this session
    const shown = sessionStorage.getItem("exitIntentShown");
    if (shown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e: MouseEvent) => {
      // Trigger when mouse leaves from top of viewport
      if (e.clientY <= 0 && !hasShown) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem("exitIntentShown", "true");
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [hasShown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input
    const validation = exitIntentSchema.safeParse({ name, email });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    setIsSubmitting(true);

    try {
      // Save lead to database
      const { error: leadError } = await supabase
        .from('leads')
        .insert({
          name,
          email,
          source: 'exit_intent_popup',
          offer_type: '20% discount'
        });

      if (leadError) throw leadError;

      // Identify visitor with lead information
      await identifyVisitor({
        email,
        name,
        conversionType: 'exit_intent_popup',
      });

      // Send email notification
      const { error: emailError } = await supabase.functions.invoke('send-lead-notification', {
        body: { name, email, source: 'Exit Intent Popup', offer: '20% Off First Purchase' }
      });

      if (emailError) {
        console.error('Email notification failed');
      }

      trackLeadGeneration('exit_intent_popup');
      toast.success("🎉 Check your email for your exclusive 20% discount code!");
      setIsOpen(false);
    } catch (error) {
      console.error('Lead capture failed');
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md glass-effect border-primary/30">
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4"
          onClick={() => setIsOpen(false)}
        >
          <X className="h-4 w-4" />
        </Button>
        
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-primary/20 p-4 rounded-full">
              <Gift className="h-12 w-12 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-2xl text-center font-orbitron">
            Wait! Don't Miss Out 🎁
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            Get <span className="text-primary font-bold">20% OFF</span> your first AI automation purchase!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-background/50"
            />
          </div>
          
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="Your Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-background/50"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 font-bold"
            disabled={isSubmitting}
          >
            <Zap className="mr-2 h-4 w-4" />
            {isSubmitting ? "Claiming..." : "Claim My 20% Discount"}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            No spam. Unsubscribe anytime. Offer valid for 7 days.
          </p>
        </form>

        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              ✅ Free Demo
            </span>
            <span className="flex items-center gap-1">
              ✅ No Credit Card
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
